<template>
  <div class="home-wrapper" :class="{ 'is-dark': isDark }">
    <section class="hero">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
      <div class="grid-overlay"></div>

      <div class="hero-inner">
        <div class="hero-badge" data-anim="fade-up" style="--d:0ms">
          <span class="badge-dot"></span>
          Plataforma de gestión de citas #1
        </div>

        <h1 class="hero-title" data-anim="fade-up" style="--d:100ms">
          Gestiona tus citas<br>
          con <em>elegancia</em><br>
          y eficiencia.
        </h1>

        <p class="hero-sub" data-anim="fade-up" style="--d:200ms">
          La plataforma que tus clientes y tu negocio merecen.<br class="hide-sm">
          Automatiza, organiza y crece sin esfuerzo.
        </p>

        <div class="hero-cta" data-anim="fade-up" style="--d:300ms">
          <router-link
            v-if="!authStore.isAuthenticated"
            to="/register"
            class="btn-primary"
          >
            Empieza Ahora — Es Gratis
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </router-link>
          <router-link v-else to="/dashboard" class="btn-primary">
            Ir al Dashboard
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </router-link>

          <a href="#features" class="btn-ghost">Ver características</a>
        </div>

        <div class="social-proof" data-anim="fade-up" style="--d:400ms">
          <div class="avatars">
            <img src="https://i.pravatar.cc/40?img=1" alt="">
            <img src="https://i.pravatar.cc/40?img=2" alt="">
            <img src="https://i.pravatar.cc/40?img=3" alt="">
            <img src="https://i.pravatar.cc/40?img=4" alt="">
          </div>
          <div>
            <div class="stars">★★★★★</div>
            <span><strong>+2,000</strong> negocios confían en nosotros</span>
          </div>
        </div>
      </div>

      <div class="hero-card" data-anim="slide-left" style="--d:200ms">
        <div class="card-header">
          <div class="card-dot red"></div>
          <div class="card-dot yellow"></div>
          <div class="card-dot green"></div>
          <span class="card-title">Próximas citas hoy</span>
        </div>
        <div class="appointment-list">
          <div class="appt-item" style="--i:0">
            <div class="appt-time">09:00</div>
            <div class="appt-info">
              <strong>María García</strong>
              <span>Corte + Tinte</span>
            </div>
            <div class="appt-badge confirmed">Confirmada</div>
          </div>
          <div class="appt-item" style="--i:1">
            <div class="appt-time">10:30</div>
            <div class="appt-info">
              <strong>Carlos López</strong>
              <span>Consulta General</span>
            </div>
            <div class="appt-badge pending">Pendiente</div>
          </div>
          <div class="appt-item" style="--i:2">
            <div class="appt-time">12:00</div>
            <div class="appt-info">
              <strong>Ana Martínez</strong>
              <span>Limpieza Dental</span>
            </div>
            <div class="appt-badge confirmed">Confirmada</div>
          </div>
          <div class="appt-item" style="--i:3">
            <div class="appt-time">15:30</div>
            <div class="appt-info">
              <strong>Pedro Sánchez</strong>
              <span>Entrenamiento Personal</span>
            </div>
            <div class="appt-badge confirmed">Confirmada</div>
          </div>
        </div>
        <div class="card-stats">
          <div class="stat">
            <span class="stat-num">12</span>
            <span class="stat-label">Citas hoy</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat">
            <span class="stat-num">98%</span>
            <span class="stat-label">Asistencia</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat">
            <span class="stat-num">4.9★</span>
            <span class="stat-label">Valoración</span>
          </div>
        </div>
      </div>
    </section>

    <section class="stats-bar">
      <div class="stats-inner">
        <div class="stat-item">
          <strong>+2,000</strong>
          <span>Negocios activos</span>
        </div>
        <div class="stat-sep"></div>
        <div class="stat-item">
          <strong>+50,000</strong>
          <span>Citas gestionadas</span>
        </div>
        <div class="stat-sep"></div>
        <div class="stat-item">
          <strong>98%</strong>
          <span>Satisfacción</span>
        </div>
        <div class="stat-sep"></div>
        <div class="stat-item">
          <strong>24/7</strong>
          <span>Reservas online</span>
        </div>
      </div>
    </section>

    <section id="features" class="features">
      <div class="section-label">Características</div>
      <h2 class="section-title">Todo lo que necesitas<br>en un solo lugar</h2>
      <p class="section-sub">Herramientas poderosas diseñadas para simplificar tu día a día y hacer crecer tu negocio.</p>

      <div class="features-grid">
        <div class="feature-card feature-large">
          <div class="feature-icon">🗓️</div>
          <h3>Calendario Inteligente</h3>
          <p>Permite que tus clientes reserven 24/7 sin posibilidad de doble reserva. Sincronización automática en tiempo real con todos tus dispositivos.</p>
          <div class="feature-tag">Sin conflictos</div>
        </div>

        <div class="feature-card">
          <div class="feature-icon">🔔</div>
          <h3>Notificaciones Automáticas</h3>
          <p>Confirmaciones y recordatorios por email. Reduce las ausencias hasta un 60%.</p>
          <div class="feature-tag">-60% ausencias</div>
        </div>

        <div class="feature-card">
          <div class="feature-icon">👥</div>
          <h3>Base de Clientes</h3>
          <p>Historial completo de citas, preferencias y contactos. Conoce mejor a cada cliente.</p>
          <div class="feature-tag">CRM integrado</div>
        </div>

        <div class="feature-card">
          <div class="feature-icon">🔧</div>
          <h3>Gestión de Servicios</h3>
          <p>Configura servicios, duraciones y precios. Control total de tu catálogo.</p>
          <div class="feature-tag">Totalmente flexible</div>
        </div>

        <div class="feature-card feature-accent">
          <div class="feature-icon">⏰</div>
          <h3>Reserva Online 24/7</h3>
          <p>Tus clientes agendan desde cualquier dispositivo, a cualquier hora. Sin llamadas, sin esperas.</p>
          <div class="feature-tag">Siempre disponible</div>
        </div>

        <div class="feature-card">
          <div class="feature-icon">🔄</div>
          <h3>Gestión Flexible</h3>
          <p>Cancela o reagenda citas en segundos. Adaptabilidad total para ti y tus clientes.</p>
          <div class="feature-tag">1 clic</div>
        </div>
      </div>
    </section>

    <section class="businesses">
      <div class="businesses-inner">
        <div class="businesses-text">
          <div class="section-label">Para todos</div>
          <h2 class="section-title left">Perfecto para<br>cualquier negocio</h2>
          <p class="section-sub left">Desde peluquerías hasta consultorios médicos, nuestra plataforma se adapta a tu sector.</p>
          <router-link
            v-if="!authStore.isAuthenticated"
            to="/register"
            class="btn-primary"
          >
            Empezar gratis
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </router-link>
        </div>

        <div class="business-cards">
          <div class="biz-card" style="--bi:0">
            <div class="biz-emoji">💇‍♀️</div>
            <div class="biz-info">
              <h4>Peluquerías & Spas</h4>
              <p>Cortes, peinados, tratamientos de belleza</p>
            </div>
            <div class="biz-arrow">→</div>
          </div>
          <div class="biz-card" style="--bi:1">
            <div class="biz-emoji">🦷</div>
            <div class="biz-info">
              <h4>Dentistas & Médicos</h4>
              <p>Consultas, revisiones, tratamientos</p>
            </div>
            <div class="biz-arrow">→</div>
          </div>
          <div class="biz-card" style="--bi:2">
            <div class="biz-emoji">🏋️‍♂️</div>
            <div class="biz-info">
              <h4>Gimnasios & Coaches</h4>
              <p>Clases grupales, entrenamientos personales</p>
            </div>
            <div class="biz-arrow">→</div>
          </div>
          <div class="biz-card" style="--bi:3">
            <div class="biz-emoji">🔧</div>
            <div class="biz-info">
              <h4>Talleres & Técnicos</h4>
              <p>Mecánica, reparaciones, mantenimiento</p>
            </div>
            <div class="biz-arrow">→</div>
          </div>
          <div class="biz-card" style="--bi:4">
            <div class="biz-emoji">⚖️</div>
            <div class="biz-info">
              <h4>Abogados & Asesores</h4>
              <p>Consultas legales, asesoría fiscal</p>
            </div>
            <div class="biz-arrow">→</div>
          </div>
          <div class="biz-card" style="--bi:5">
            <div class="biz-emoji">🎨</div>
            <div class="biz-info">
              <h4>Artistas & Creativos</h4>
              <p>Sesiones fotográficas, clases de arte</p>
            </div>
            <div class="biz-arrow">→</div>
          </div>
        </div>
      </div>
    </section>

    <section class="testimonials">
      <div class="section-label">Opiniones</div>
      <h2 class="section-title">Lo que dicen nuestros clientes</h2>

      <div class="testimonials-grid">
        <div class="testimonial-card">
          <div class="t-stars">★★★★★</div>
          <p>"Desde que uso GIOR, las ausencias bajaron un 70%. Mis clientes adoran poder reservar desde el móvil a cualquier hora."</p>
          <div class="t-author">
            <img src="https://i.pravatar.cc/48?img=5" alt="">
            <div>
              <strong>Sofía Ramírez</strong>
              <span>Peluquería Style & Art</span>
            </div>
          </div>
        </div>
        <div class="testimonial-card testimonial-featured">
          <div class="t-stars">★★★★★</div>
          <p>"Increíblemente fácil de usar. En 15 minutos tenía todo configurado y mis pacientes ya podían reservar online. Totalmente recomendado."</p>
          <div class="t-author">
            <img src="https://i.pravatar.cc/48?img=12" alt="">
            <div>
              <strong>Dr. Miguel Torres</strong>
              <span>Clínica Dental Sonrisas</span>
            </div>
          </div>
        </div>
        <div class="testimonial-card">
          <div class="t-stars">★★★★★</div>
          <p>"El sistema de recordatorios automáticos ha revolucionado mi negocio. Ahorro 2 horas diarias que antes pasaba confirmando citas por teléfono."</p>
          <div class="t-author">
            <img src="https://i.pravatar.cc/48?img=9" alt="">
            <div>
              <strong>Laura Vega</strong>
              <span>Centro de Bienestar Zen</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="cta-final">
      <div class="cta-orb cta-orb-1"></div>
      <div class="cta-orb cta-orb-2"></div>
      <div class="cta-inner">
        <h2>¿Listo para digitalizar<br>tu negocio?</h2>
        <p>Únete a más de 2,000 negocios que ya confían en nosotros. Sin tarjeta de crédito. Sin compromisos.</p>
        <div class="cta-buttons">
          <router-link
            v-if="!authStore.isAuthenticated"
            to="/register"
            class="btn-cta-primary"
          >
            Comenzar Ahora — Es Gratis
          </router-link>
          <router-link v-else to="/dashboard" class="btn-cta-primary">
            Ir al Dashboard
          </router-link>
          <a href="#features" class="btn-cta-ghost">Conocer más</a>
        </div>
        <div class="cta-perks">
          <span>✓ Configuración en 15 min</span>
          <span>✓ Sin tarjeta de crédito</span>
          <span>✓ Cancela cuando quieras</span>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useTheme } from '@/composables/useTheme'

const authStore = useAuthStore()
const { isDark } = useTheme()

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(el => {
        if (el.isIntersecting) el.target.classList.add('visible')
      })
    },
    { threshold: 0.15 }
  )
  document.querySelectorAll('[data-anim]').forEach(el => observer.observe(el))
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=DM+Sans:ital,wght@0,400;0,500;1,400&display=swap');

.home-wrapper {
  --c-bg: #f8f6f3;
  --c-surface: #ffffff;
  --c-border: #e8e2db;
  --c-text: #1f1d20;
  --c-muted: #5a555b;
  --c-accent: #8c5a3d;
  --c-accent-hover: #704832;
  --c-accent-light: rgba(140, 90, 61, 0.12);
  --c-accent-glow: rgba(140, 90, 61, 0.1);
  --c-warning: #f4a261;
  --c-static-btn-bg: #353940;
  --c-static-btn-hover: #2a2d33;
  --radius: 16px;
  --radius-sm: 10px;
  --shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  --shadow-lg: 0 12px 48px rgba(0, 0, 0, 0.13);
  --font-display: 'Sora', sans-serif;
  --font-body: 'DM Sans', sans-serif;
  font-family: var(--font-body);
  color: var(--c-text);
  background:
    radial-gradient(circle at 72% 38%, rgba(140, 90, 61, 0.08), transparent 18%),
    linear-gradient(180deg, #fcfbf9 0%, var(--c-bg) 100%);
  overflow-x: hidden;
}

.home-wrapper.is-dark {
  --c-bg: #231f22;
  --c-surface: #2a2528;
  --c-border: rgba(255, 255, 255, 0.12);
  --c-text: #f4f1eb;
  --c-muted: rgba(244, 241, 235, 0.78);
  --c-accent: #8c5a3d;
  --c-accent-hover: #704832;
  --c-accent-light: rgba(140, 90, 61, 0.18);
  --c-accent-glow: rgba(140, 90, 61, 0.12);
  --shadow: none;
  --shadow-lg: none;
  background:
    radial-gradient(circle at 72% 78%, rgba(140, 90, 61, 0.14), transparent 20%),
    linear-gradient(180deg, #231f22 0%, #231f22 100%) !important;
}

.home-wrapper.is-dark .hero,
.home-wrapper.is-dark .stats-bar,
.home-wrapper.is-dark .features,
.home-wrapper.is-dark .businesses,
.home-wrapper.is-dark .testimonials {
  background: transparent !important;
}

.home-wrapper.is-dark .feature-card,
.home-wrapper.is-dark .feature-large,
.home-wrapper.is-dark .feature-accent,
.home-wrapper.is-dark .biz-card,
.home-wrapper.is-dark .testimonial-card,
.home-wrapper.is-dark .testimonial-featured {
  background: var(--c-surface) !important;
  border-color: var(--c-border) !important;
  box-shadow: none !important;
}

.home-wrapper.is-dark .feature-accent h3,
.home-wrapper.is-dark .feature-accent p,
.home-wrapper.is-dark .feature-accent .feature-tag,
.home-wrapper.is-dark .testimonial-featured p,
.home-wrapper.is-dark .testimonial-featured .t-author strong,
.home-wrapper.is-dark .testimonial-featured .t-author span {
  color: var(--c-text);
}

.home-wrapper.is-dark .feature-accent .feature-tag {
  background: var(--c-accent-light) !important;
  color: var(--c-accent) !important;
}

[data-anim] {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.65s cubic-bezier(.22,.61,.36,1) var(--d, 0ms),
              transform 0.65s cubic-bezier(.22,.61,.36,1) var(--d, 0ms);
}
[data-anim="slide-left"] { transform: translateX(40px); }
[data-anim].visible { opacity: 1; transform: none; }

.hero {
  position: relative;
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  align-items: center;
  padding: 8rem 6vw 6rem;
  overflow: hidden;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;
}
.orb-1 {
  width: 600px;
  height: 600px;
  background: var(--c-accent-glow);
  top: -200px;
  left: -200px;
  animation: float 8s ease-in-out infinite;
}
.orb-2 {
  width: 400px;
  height: 400px;
  background: rgba(244, 162, 97, 0.08);
  bottom: -100px;
  right: 30%;
  animation: float 10s ease-in-out infinite reverse;
}
.orb-3 {
  width: 300px;
  height: 300px;
  background: var(--c-accent-glow);
  top: 40%;
  right: -80px;
  animation: float 12s ease-in-out infinite 2s;
}
@keyframes float {
  0%,100% { transform: translate(0,0) scale(1); }
  50% { transform: translate(20px,-20px) scale(1.05); }
}

.grid-overlay {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(var(--c-border) 1px, transparent 1px),
    linear-gradient(90deg, var(--c-border) 1px, transparent 1px);
  background-size: 48px 48px;
  opacity: 0.22;
  mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 20%, transparent 100%);
}

.hero-inner {
  position: relative;
  z-index: 1;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--c-text);
  padding: 0;
  font-family: var(--font-display);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
}
.badge-dot {
  width: 7px;
  height: 7px;
  background: var(--c-text);
  border-radius: 50%;
  animation: pulse-dot 2s ease-in-out infinite;
}
@keyframes pulse-dot {
  0%,100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.8); }
}

.hero-title {
  font-family: var(--font-display);
  font-size: clamp(2.8rem, 5.5vw, 5rem);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.03em;
  margin-bottom: 1.5rem;
  color: var(--c-text);
}
.hero-title em {
  font-style: normal;
  color: var(--c-accent);
}

.hero-sub {
  font-size: clamp(1rem, 1.6vw, 1.2rem);
  color: var(--c-muted);
  line-height: 1.7;
  max-width: 520px;
  margin-bottom: 2.5rem;
}

.hero-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  margin-bottom: 2.5rem;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--c-accent);
  color: #ffffff;
  padding: 15px 30px;
  border-radius: var(--radius-sm);
  font-family: var(--font-display);
  font-size: 0.97rem;
  font-weight: 700;
  text-decoration: none;
  letter-spacing: 0.01em;
  transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.16);
}
.btn-primary:hover {
  transform: translateY(-2px);
  background: var(--c-accent-hover);
  box-shadow: 0 8px 26px rgba(0, 0, 0, 0.2);
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--c-text);
  font-size: 0.95rem;
  font-weight: 500;
  text-decoration: none;
  padding: 13px 8px;
  border-radius: var(--radius-sm);
  transition: color 0.2s;
}
.btn-ghost:hover {
  color: var(--c-accent);
}

.social-proof {
  display: flex;
  align-items: center;
  gap: 14px;
}
.avatars { display: flex; }
.avatars img {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid var(--c-surface);
  margin-left: -10px;
  object-fit: cover;
}
.avatars img:first-child { margin-left: 0; }
.stars { color: #e8930a; font-size: 0.85rem; margin-bottom: 2px; }
.social-proof span { font-size: 0.82rem; color: var(--c-muted); }
.social-proof strong { color: var(--c-text); }

.hero-card {
  position: relative;
  z-index: 1;
  background: transparent;
  border-radius: var(--radius);
  overflow: hidden;
  max-width: 420px;
  justify-self: center;
}
.card-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 14px 18px;
  border-bottom: 1px solid color-mix(in srgb, var(--c-border) 60%, transparent);
}
.card-dot { width: 11px; height: 11px; border-radius: 50%; }
.card-dot.red { background: #ff5f57; }
.card-dot.yellow { background: #febc2e; }
.card-dot.green { background: #28c840; }
.card-title {
  margin-left: 8px;
  font-family: var(--font-display);
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--c-text);
}

.appointment-list { padding: 12px 0; }
.appt-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 18px;
  animation: slide-in 0.5s cubic-bezier(.22,.61,.36,1) both;
  animation-delay: calc(var(--i) * 100ms + 400ms);
  transition: background 0.15s;
}
@keyframes slide-in {
  from { opacity: 0; transform: translateX(-12px); }
  to { opacity: 1; transform: none; }
}
.appt-item:hover { background: color-mix(in srgb, var(--c-border) 18%, transparent); }
.appt-time {
  font-family: var(--font-display);
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--c-text);
  min-width: 40px;
}
.appt-info { flex: 1; }
.appt-info strong { display: block; font-size: 0.88rem; font-weight: 600; color: var(--c-text); }
.appt-info span { font-size: 0.78rem; color: var(--c-muted); }
.appt-badge {
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 100px;
}
.appt-badge.confirmed {
  background: var(--c-accent-light);
  color: var(--c-text);
}
.appt-badge.pending {
  background: rgba(244, 162, 97, 0.2);
  color: #a04a10;
}

.card-stats {
  display: flex;
  align-items: center;
  padding: 14px 18px;
  border-top: 1px solid color-mix(in srgb, var(--c-border) 60%, transparent);
}
.stat { flex: 1; text-align: center; }
.stat-num {
  display: block;
  font-family: var(--font-display);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--c-text);
}
.stat-label { font-size: 0.72rem; color: var(--c-muted); }
.stat-divider { width: 1px; height: 32px; background: color-mix(in srgb, var(--c-border) 60%, transparent); }

.stats-bar {
  padding: 1.5rem 6vw;
}
.stats-inner {
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 1rem;
}
.stat-item { text-align: center; color: var(--c-text); }
.stat-item strong {
  display: block;
  font-family: var(--font-display);
  font-size: 1.7rem;
  font-weight: 800;
  line-height: 1;
}
.stat-item span { font-size: 0.82rem; opacity: 0.85; }
.stat-sep { width: 1px; height: 40px; background: color-mix(in srgb, var(--c-border) 70%, transparent); }

.section-label {
  font-family: var(--font-display);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--c-accent);
  margin-bottom: 1rem;
  text-align: center;
}
.section-title {
  font-family: var(--font-display);
  font-size: clamp(2rem, 3.5vw, 2.8rem);
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.15;
  text-align: center;
  margin-bottom: 1rem;
  color: var(--c-text);
}
.section-title.left { text-align: left; }
.section-sub {
  font-size: 1.05rem;
  color: var(--c-muted);
  text-align: center;
  max-width: 560px;
  margin: 0 auto 3.5rem;
  line-height: 1.7;
}
.section-sub.left { text-align: left; margin: 0 0 2rem; }

.features {
  padding: 7rem 6vw;
  background: var(--c-bg);
}
.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  max-width: 1100px;
  margin: 0 auto;
}
.feature-card {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  padding: 2rem;
  transition: transform 0.25s, box-shadow 0.25s, border-color 0.25s;
  position: relative;
  overflow: hidden;
}
.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, var(--c-accent), var(--c-accent-hover));
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}
.feature-card:hover::before { transform: scaleX(1); }
.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
  border-color: var(--c-accent);
}
.feature-large {
  grid-column: span 2;
  background: linear-gradient(135deg, var(--c-surface) 60%, var(--c-accent-light));
}
.feature-accent {
  background: var(--c-surface);
  border-color: var(--c-border);
}
.feature-accent h3,
.feature-accent p,
.feature-accent .feature-tag {
  color: inherit;
}
.feature-accent .feature-tag {
  background: var(--c-accent-light);
  color: var(--c-accent);
}
.feature-icon { font-size: 2rem; margin-bottom: 1rem; }
.feature-card h3 {
  font-family: var(--font-display);
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.6rem;
  color: var(--c-text);
}
.feature-card p {
  font-size: 0.9rem;
  color: var(--c-muted);
  line-height: 1.65;
  margin-bottom: 1.2rem;
}
.feature-tag {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: var(--c-accent-light);
  color: var(--c-accent);
  padding: 4px 10px;
  border-radius: 100px;
}

.businesses {
  padding: 7rem 6vw;
  background: var(--c-surface);
}
.businesses-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 5rem;
  align-items: center;
}
.business-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.8rem;
}
.biz-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--c-bg);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
  animation: fade-up 0.5s cubic-bezier(.22,.61,.36,1) both;
  animation-delay: calc(var(--bi) * 80ms);
}
.biz-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow);
  border-color: var(--c-accent);
}
.biz-emoji { font-size: 1.5rem; min-width: 2rem; }
.biz-info { flex: 1; }
.biz-info h4 {
  font-family: var(--font-display);
  font-size: 0.88rem;
  font-weight: 700;
  margin-bottom: 2px;
  color: var(--c-text);
}
.biz-info p { font-size: 0.76rem; color: var(--c-muted); margin: 0; }
.biz-arrow {
  color: var(--c-accent);
  font-size: 1.1rem;
  opacity: 0;
  transition: opacity 0.2s, transform 0.2s;
}
.biz-card:hover .biz-arrow { opacity: 1; transform: translateX(4px); }

.testimonials {
  padding: 7rem 6vw;
  background: var(--c-bg);
}
.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  max-width: 1100px;
  margin: 0 auto;
  align-items: start;
}
.testimonial-card {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  padding: 2rem;
}
.testimonial-featured {
  background: var(--c-surface);
  border-color: var(--c-border);
  transform: scale(1.04);
}
.testimonial-featured .t-stars { color: #fde68a; }
.testimonial-featured p { color: var(--c-muted); font-style: italic; }
.testimonial-featured .t-author strong { color: var(--c-text); }
.testimonial-featured .t-author span { color: var(--c-muted); }
.t-stars { color: #e8930a; font-size: 0.95rem; margin-bottom: 1rem; }
.testimonial-card p {
  font-size: 0.92rem;
  line-height: 1.7;
  color: var(--c-muted);
  margin-bottom: 1.5rem;
  font-style: italic;
}
.t-author { display: flex; align-items: center; gap: 12px; }
.t-author img {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--c-border);
}
.t-author strong { display: block; font-size: 0.88rem; font-weight: 700; color: var(--c-text); }
.t-author span { font-size: 0.78rem; color: var(--c-muted); }

.cta-final {
  position: relative;
  background: #ffffff;
  color: var(--c-text);
  padding: 7rem 6vw;
  text-align: center;
  overflow: hidden;
}
.cta-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
}
.cta-orb-1 {
  width: 500px;
  height: 500px;
  background: var(--c-accent-glow);
  top: -200px;
  left: -100px;
}
.cta-orb-2 {
  width: 400px;
  height: 400px;
  background: rgba(244, 162, 97, 0.06);
  bottom: -150px;
  right: -50px;
}
.cta-inner { position: relative; z-index: 1; }
.cta-final h2 {
  font-family: var(--font-display);
  font-size: clamp(2rem, 4vw, 3.2rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.15;
  margin-bottom: 1.2rem;
  color: inherit;
}
.cta-final p {
  font-size: 1.1rem;
  opacity: 0.75;
  max-width: 500px;
  margin: 0 auto 2.5rem;
  line-height: 1.6;
}
.cta-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
}
.btn-cta-primary {
  display: inline-block;
  background: var(--c-accent);
  color: #fff;
  padding: 16px 36px;
  border-radius: var(--radius-sm);
  font-family: var(--font-display);
  font-size: 1rem;
  font-weight: 700;
  text-decoration: none;
  transition: transform 0.2s, opacity 0.2s, background 0.2s;
  box-shadow: 0 4px 20px rgba(0,0,0,0.25);
}
.btn-cta-primary:hover {
  transform: translateY(-2px);
  background: var(--c-accent-hover);
}
.btn-cta-ghost {
  display: inline-block;
  border: 1.5px solid var(--c-border);
  color: inherit;
  padding: 16px 36px;
  border-radius: var(--radius-sm);
  font-family: var(--font-display);
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  transition: border-color 0.2s, background 0.2s;
}
.btn-cta-ghost:hover {
  border-color: var(--c-accent);
  background: var(--c-accent-light);
}
.cta-perks {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  font-size: 0.85rem;
  opacity: 0.65;
}

.home-wrapper.is-dark .cta-final {
  background:
    radial-gradient(circle at 76% 78%, rgba(140, 90, 61, 0.12), transparent 16%),
    linear-gradient(180deg, #231f22 0%, #231f22 100%) !important;
  color: #f5f1eb !important;
}

.home-wrapper.is-dark .btn-cta-primary {
  background: var(--c-static-btn-bg);
}

.home-wrapper.is-dark .btn-cta-primary:hover {
  background: var(--c-static-btn-hover);
}

.home-wrapper.is-dark .btn-cta-ghost {
  border-color: rgba(255,255,255,0.3);
}

.home-wrapper.is-dark .btn-cta-ghost:hover {
  border-color: rgba(255,255,255,0.6);
  background: rgba(255,255,255,0.07);
}

@media (max-width: 1024px) {
  .hero {
    grid-template-columns: 1fr;
    padding: 7rem 6vw 4rem;
    text-align: center;
    justify-items: center;
  }
  .hero-sub, .hero-badge { margin-left: auto; margin-right: auto; }
  .hero-cta { justify-content: center; }
  .social-proof { justify-content: center; }
  .hero-card { max-width: 500px; width: 100%; }
  [data-anim="slide-left"] { transform: translateY(28px); }
  .features-grid { grid-template-columns: 1fr 1fr; }
  .feature-large { grid-column: span 2; }
  .businesses-inner { grid-template-columns: 1fr; gap: 3rem; }
  .section-title.left { text-align: center; }
  .section-sub.left { text-align: center; margin: 0 auto 2rem; }
  .businesses-text { text-align: center; }
  .businesses-text .btn-primary { margin: 0 auto; }
}

@media (max-width: 768px) {
  .features-grid { grid-template-columns: 1fr; }
  .feature-large { grid-column: span 1; }
  .testimonials-grid { grid-template-columns: 1fr; }
  .testimonial-featured { transform: none; }
  .business-cards { grid-template-columns: 1fr; }
  .stat-sep { display: none; }
  .stats-inner { gap: 1.5rem; }
  .hero { padding: 6rem 5vw 3rem; }
}

@media (max-width: 480px) {
  .hero-title { font-size: 2.4rem; }
  .btn-primary { width: 100%; justify-content: center; }
  .hero-cta { flex-direction: column; }
  .cta-buttons { flex-direction: column; align-items: center; }
  .btn-cta-primary, .btn-cta-ghost { width: 100%; max-width: 320px; text-align: center; }
}

.hide-sm { display: none; }
@media (min-width: 600px) { .hide-sm { display: inline; } }
</style>
